import { memo, useEffect, useState } from "react";

import { LoadingOverlay } from "../../../../components/LoadingOverlay/LoadingOverlay";
import { useUser } from "../../../../contexts/userContext";
import type { IQueryParams } from "../../../../hooks/reactQueryHooks/useOrganization";
import { useOrganizationMembers } from "../../../../hooks/reactQueryHooks/useOrganization";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import type { IData } from "../../../../types";
import type { IEditUser } from "../MembersTable/MembersTable";
import { MembersTable } from "../MembersTable/MembersTable";

interface MembersPageProps {
  setMembersAmount: (amount: number) => void;
  currentOrganizationId: string;
  orgOwnerId: string;
}

export const MembersPage: React.FC<MembersPageProps> = memo(
  ({ setMembersAmount, currentOrganizationId, orgOwnerId }) => {
    const [internalMembers, setInternalMembers] = useState<IData[]>([]);
    const { queryValue: searchQuery } = useQueryParams({
      queryStr: "s",
    });
    const { queryValue: pageIndex } = useQueryParams({
      queryStr: "p",
    });
    const { user } = useUser();

    const params: IQueryParams = {
      pageSize: 10,
      pageIndex: pageIndex ? Number(pageIndex) - 1 : 0,
      search: searchQuery || "",
    };

    const {
      data: queryData,
      isLoading,
      isFetching,
    } = useOrganizationMembers(currentOrganizationId, params);

    useEffect(() => {
      if (queryData) {
        setMembersAmount(queryData.totalCount);
        return;
      }
      setMembersAmount(0);
    }, [queryData, setMembersAmount]);

    useEffect(() => {
      setInternalMembers(
        queryData?.items.map(item => ({
          ...item,
          owner: item.email === user?.email,
        })) || [],
      );
    }, [queryData?.items, user?.email]);

    const updateMember = (member: IEditUser) => {
      const newData = internalMembers.map((item: any) => {
        if (item.id === member.id) {
          return {
            ...item,
            name: `${member.firstName} ${member.lastName}`,
            firstName: member.firstName,
            lastName: member.lastName,
          };
        }
        return item;
      });
      setInternalMembers(newData);
    };

    return (
      <>
        <MembersTable
          isLoading={isLoading}
          pageSize={params.pageSize}
          data={internalMembers as IData[]}
          totalCount={queryData?.totalCount || 0}
          updateMember={updateMember}
          orgOwnerId={orgOwnerId}
        />
        {(isLoading || isFetching) && <LoadingOverlay />}
      </>
    );
  },
);
